import React, { useEffect, useState } from "react";
import {
  Button,
  Header,
  Modal,
  Form,
  TextArea,
  Grid,
  Segment,
  Checkbox,
  Message,
} from "semantic-ui-react";
import { useDispatch, useSelector } from "react-redux";
import { closeModal } from "../../../app/common/modals/modalSlice";
import {
  addEmailToDb,
  addNotificationToDb,
  sendComplianceNotificationEmail,
  getUserProfile,
} from "../../../app/firestore/firestoreService";
import { convertAddressFull } from "../../../app/common/util/util";
import { serverTimestamp, onSnapshot } from "firebase/firestore";
import { toast } from "react-toastify";
import ModalWrapper from "../../../app/common/modals/modalWrapper";
import { useMediaQuery } from "react-responsive";

const notificationTypeOptions = [
  { key: "review", text: "Review Requested", value: "Review Requested" },
  { key: "changes", text: "Changes Requested", value: "Changes Requested" },
  { key: "correction", text: "Correction Required", value: "Correction Required" },
  { key: "approval", text: "Approval Needed", value: "Approval Needed" },
];

export default function TransactionNotifyModal({ transaction }) {
  const dispatch = useDispatch();
  const { currentUserProfile } = useSelector((state) => state.profile);
  const { docsTransActive } = useSelector((state) => state.doc);
  const isMobile = useMediaQuery({ query: "(max-width:768px)" });

  const transactionAddress = transaction?.address
    ? convertAddressFull(transaction.address)
    : transaction?.title || "";

  const [notificationType, setNotificationType] = useState("");
  const [selectedRecipients, setSelectedRecipients] = useState({
    agent: false,
    coAgent: false,
    tc: false,
    assistant: false,
    manager: false,
  });
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [deliveryInApp, setDeliveryInApp] = useState(true);
  const [deliveryEmail, setDeliveryEmail] = useState(true);
  const [customMessage, setCustomMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [managerProfile, setManagerProfile] = useState(null);
  const managerId = transaction?.managerId;

  // Managers opt in to being included in the Notify feature; check their
  // live preference (not a denormalized snapshot) before showing them.
  useEffect(() => {
    if (!managerId) {
      setManagerProfile(null);
      return;
    }

    const unsubscribe = onSnapshot(
      getUserProfile(managerId),
      (snapshot) => {
        setManagerProfile(snapshot.exists() ? snapshot.data() : null);
      },
      (error) => {
        console.error("Error fetching manager profile:", error);
      }
    );

    return () => unsubscribe();
  }, [managerId]);

  const managerIncludedInNotify =
    managerProfile?.notificationPreferences?.includeInTeamNotify === true;

  const availableRecipients = {
    agent: {
      label: "Agent",
      userId: transaction?.userId,
      email: transaction?.agentProfile?.email,
      name: `${transaction?.agentProfile?.firstName || ""} ${transaction?.agentProfile?.lastName || ""}`.trim(),
    },
    coAgent: {
      label: "Co-Agent",
      userId: transaction?.coAgentId,
      email: transaction?.coAgent?.email,
      name: `${transaction?.coAgent?.firstName || ""} ${transaction?.coAgent?.lastName || ""}`.trim(),
      available: !!transaction?.coAgentId,
    },
    tc: {
      label: "Transaction Coordinator",
      userId: transaction?.tcId,
      email: transaction?.transactionCoordinator?.email,
      name: `${transaction?.transactionCoordinator?.firstName || ""} ${transaction?.transactionCoordinator?.lastName || ""}`.trim(),
      available: !!transaction?.tcId,
    },
    assistant: {
      label: "Assistant",
      userId: transaction?.assistant?.userId,
      email: transaction?.assistant?.email,
      name: `${transaction?.assistant?.firstName || ""} ${transaction?.assistant?.lastName || ""}`.trim(),
      available: !!transaction?.hasAssistant,
    },
    manager: {
      label: "Manager",
      userId: transaction?.managerId,
      email: transaction?.managerDetails?.managerEmail,
      name: `${transaction?.managerDetails?.firstName || ""} ${transaction?.managerDetails?.lastName || ""}`.trim(),
      available: !!transaction?.managerId && managerIncludedInNotify,
    },
  };

  const docOptions = (docsTransActive?.docs || []).map((doc) => ({
    key: doc.id,
    text: doc.name,
    value: doc.id,
  }));

  function handleRecipientToggle(recipientKey) {
    setSelectedRecipients((prev) => ({
      ...prev,
      [recipientKey]: !prev[recipientKey],
    }));
  }

  async function handleSend() {
    if (!notificationType) {
      toast.error("Please select a notification type");
      return;
    }

    const hasRecipient = Object.values(selectedRecipients).some((v) => v);
    if (!hasRecipient) {
      toast.error("Please select at least one recipient");
      return;
    }

    if (!deliveryInApp && !deliveryEmail) {
      toast.error("Please select at least one delivery option");
      return;
    }

    setIsSubmitting(true);

    try {
      const selectedDocNames = selectedDocIds
        .map((docId) => docsTransActive?.docs?.find((doc) => doc.id === docId)?.name)
        .filter(Boolean);

      const recipientIds = [];
      const toEmails = [];
      const toEmailsAndRoles = [];
      Object.entries(selectedRecipients).forEach(([key, isSelected]) => {
        if (!isSelected) return;
        const recipient = availableRecipients[key];
        if (recipient?.userId) recipientIds.push(recipient.userId);
        if (recipient?.email) {
          toEmails.push(recipient.email);
          toEmailsAndRoles.push({
            role: recipient.label,
            email: recipient.email,
            firstName: recipient.name?.split(" ")[0] || "",
            lastName: recipient.name?.split(" ").slice(1).join(" ") || "",
          });
        }
      });

      if (deliveryInApp && recipientIds.length > 0) {
        await addNotificationToDb({
          transactionId: transaction.id,
          transactionAddress,
          type: "teamNotifyRequest",
          requestType: notificationType,
          docIds: selectedDocIds,
          docNames: selectedDocNames,
          docName: selectedDocNames.join(", "),
          message: customMessage.trim(),
          fromUserId: currentUserProfile.id,
          fromFirstName: currentUserProfile.firstName,
          fromLastName: currentUserProfile.lastName,
          status: "unresolved",
          recipientIds,
        });
      }

      if (deliveryEmail && toEmails.length > 0) {
        const subject = `${notificationType}: ${transactionAddress}`;
        const docsLine =
          selectedDocNames.length > 0
            ? `\n\nDocument(s): ${selectedDocNames.join(", ")}`
            : "";
        const emailMessage = `${customMessage.trim()}${docsLine}`;

        const emailHistoryData = {
          transactionId: transaction.id,
          transactionTitle: transaction.title,
          from: currentUserProfile.email,
          fromName: `${currentUserProfile.firstName} ${currentUserProfile.lastName}`,
          to: toEmails,
          toEmails,
          toEmailsAndRoles,
          subject,
          message: emailMessage,
          type: "team-notify-request",
          sentAt: serverTimestamp(),
        };

        const mailOptions = {
          to: toEmails,
          from: `${currentUserProfile.firstName} ${currentUserProfile.lastName} <help@transactioner.com>`,
          replyTo: currentUserProfile.email,
          message: {
            subject,
            text: emailMessage,
            html: `
              <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto;">
                <p><strong>${notificationType}</strong></p>
                <p style="white-space: pre-line;">${customMessage.trim().replace(/\n/g, "<br/>")}</p>
                ${
                  selectedDocNames.length > 0
                    ? `<p><strong>Document(s):</strong> ${selectedDocNames.join(", ")}</p>`
                    : ""
                }
                <hr/>
                <p style="color: #666; font-size: 12px;">
                  Sent from Transactioner by ${currentUserProfile.firstName} ${currentUserProfile.lastName}
                </p>
              </div>
            `,
          },
        };

        await sendComplianceNotificationEmail(mailOptions);
        await addEmailToDb(emailHistoryData, { message: mailOptions.message });
      }

      toast.success("Notification sent successfully!");
      dispatch(closeModal({ modalType: "TransactionNotifyModal" }));
    } catch (error) {
      console.error("Error sending notification:", error);
      toast.error("Failed to send notification. Please try again.");
      setIsSubmitting(false);
    }
  }

  function handleCancel() {
    dispatch(closeModal({ modalType: "TransactionNotifyModal" }));
  }

  return (
    <ModalWrapper size="large">
      <Segment clearing>
        <div
          className={
            isMobile
              ? "zero horizontal margin small top margin"
              : "medium horizontal margin small top margin"
          }
        >
          <Header icon="bullhorn" content="Notify" />
          <Modal.Content>
            <Message info>
              <Message.Header>Notify Team Members</Message.Header>
              <p>Send a notification to selected team members about this transaction.</p>
            </Message>

            <Form>
              <Grid>
                <Grid.Row>
                  <Grid.Column width={16}>
                    <label>
                      <strong>Notification Type:</strong>
                    </label>
                    <Form.Select
                      fluid
                      selection
                      placeholder="Select a notification type"
                      options={notificationTypeOptions}
                      value={notificationType}
                      onChange={(e, d) => setNotificationType(d.value)}
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={16}>
                    <label>
                      <strong>Notify:</strong>
                    </label>
                    <div className="small top margin">
                      {Object.entries(availableRecipients).map(([key, recipient]) => {
                        if (recipient.available === false) return null;
                        if (!recipient.email && !recipient.userId) return null;
                        return (
                          <div key={key} className="tiny bottom margin">
                            <Checkbox
                              label={`${recipient.label} – ${recipient.name}${
                                recipient.email ? ` (${recipient.email})` : ""
                              }`}
                              checked={selectedRecipients[key]}
                              onChange={() => handleRecipientToggle(key)}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={16}>
                    <label>
                      <strong>Related Document(s) (optional):</strong>
                    </label>
                    <Form.Select
                      fluid
                      multiple
                      search
                      selection
                      placeholder="Select document(s) this notification is about"
                      options={docOptions}
                      value={selectedDocIds}
                      onChange={(e, d) => setSelectedDocIds(d.value)}
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={16}>
                    <label>
                      <strong>Delivery Options:</strong>
                    </label>
                    <div className="small top margin">
                      <Checkbox
                        label="In-app notification"
                        checked={deliveryInApp}
                        onChange={() => setDeliveryInApp((v) => !v)}
                      />
                      <div className="tiny top margin">
                        <Checkbox
                          label="Email"
                          checked={deliveryEmail}
                          onChange={() => setDeliveryEmail((v) => !v)}
                        />
                      </div>
                    </div>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={16}>
                    <label>
                      <strong>Message:</strong>
                    </label>
                    <TextArea
                      rows={4}
                      value={customMessage}
                      onChange={(e) => setCustomMessage(e.target.value)}
                      placeholder="Enter your message here..."
                      style={{ width: "100%" }}
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={16}>
                    <div className="float-right">
                      <Button onClick={handleCancel} disabled={isSubmitting}>
                        Cancel
                      </Button>
                      <Button
                        primary
                        onClick={handleSend}
                        loading={isSubmitting}
                        disabled={isSubmitting}
                      >
                        Send
                      </Button>
                    </div>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Form>
          </Modal.Content>
        </div>
      </Segment>
    </ModalWrapper>
  );
}
